import { EVoucher, Spinner } from '@silverrailtech/srtlibrary';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { injectIntl, useIntl } from 'react-intl';

import messages from '../../../messagesCustomerAccounts';
import {
	deleteCustomerAccountData,
	getCampaigns,
	getCustomerAccountData,
	updateEVoucher,
} from '../../../../api';
import ExtendedSnackbar from '../../../../components/ExtendedSnackbar/ExtendedSnackbar';
import useRequestor from '../../../../hooks/useRequestor';
import useAuth from '../../../../hooks/useAuth';
import { GlobalEVoucherStyle } from '../../ManageCustomerAccountStyled';
import { CALCULATION_TYPES } from '../../../../Campaigns/utils';

const EVouchers = () => {
	const intl = useIntl();
	const { getAuthHeaders, accountId } = useAuth();
	const [showVoucherForm, setShowVoucherForm] = useState(false);
	const [evoucherData, setEvoucherData] = useState([]);
	const [isPending, setIsPending] = useState(true);
	const [error, setError] = useState('');

	const [campaigns, setCampaigns] = useState([]); // for eVoucher dropdown

	const { customersRequestorId: requestorId, apiCallWithAuthRetry } =
		useRequestor();

	const fetchCampaigns = useCallback(() => {
		setIsPending(true);
		getCampaigns(
			{
				status: 'ACTIVE',
				type: 'VOUCHER',
			},
			requestorId,
			(err) => {
				setError(err.message || 'Failed to load campaigns');
				setIsPending(false);
				setCampaigns([]);
			},
			(data) => {
				try {
					setCampaigns(data);
					setError('');
				} catch (processingError) {
					setError('Error processing campaign data');
					setCampaigns([]);
				} finally {
					setIsPending(false);
				}
			},
		);
	}, [requestorId]);

	const onCancel = () => {
		setShowVoucherForm(false);
	};

	const fetchEvouchers = () => {
		apiCallWithAuthRetry({
			apiCall: (onError, onSuccess, refreshedHeaders) => {
				getCustomerAccountData(
					{ requestorId },
					refreshedHeaders || getAuthHeaders(),
					`/${accountId}/vouchers`,
					onError,
					onSuccess,
				);
			},
			onError: (err) => {
				setError(err.message || 'Failed to load eVouchers');
				setIsPending(false);
			},
			onSuccess: (data) => {
				setEvoucherData(data);
				setIsPending(false);
			},
		});
	};

	const eVoucherOptions = useMemo(
		() => [
			{ name: 'None', value: 'None' },
			...campaigns.map((item) => ({
				name: item.source.campaign_name,
				value: item.source.campaign_code,
				type:
					item.source.campaign_calculation_type ===
						CALCULATION_TYPES.UPON_ISSUANCE &&
					item.source.campaign_calculation_type,
			})),
		],
		[campaigns],
	);

	const submitEVoucher = (eVoucherCode) => {
		const selectedEVoucher = campaigns.find(
			(evoucher) => evoucher.source.campaign_code === eVoucherCode.code,
		);

		const isUponIssuance =
			selectedEVoucher.source.campaign_calculation_type ===
			CALCULATION_TYPES.UPON_ISSUANCE;

		const issuerVoucher = {
			campaign_code: eVoucherCode.code,
			description: selectedEVoucher.source.campaign_name,
			value_currency: 'GBP', // static atm.
			value_amount: isUponIssuance
				? eVoucherCode.issuanceValue
				: selectedEVoucher.source.campaign_value,
			value_amount_child: 0,
			issued_customer_profile_id: accountId,
			redeemable_customer_profile_id: accountId,
		};

		apiCallWithAuthRetry({
			apiCall: (onError, onSuccess) => {
				updateEVoucher(issuerVoucher, requestorId, onError, onSuccess);
			},
			onError: (err) => {
				setIsPending(false);
				setError(err.message || 'Failed to set eVouchers');
			},
			onSuccess: () => {
				fetchEvouchers();
			},
		});
	};

	const onSubmit = (eVoucherCode) => {
		setIsPending(true);
		setShowVoucherForm(false);
		submitEVoucher(eVoucherCode);
	};

	const onDeleteEVoucher = (voucherId) => {
		const voucher = evoucherData.find((item) => item.id === voucherId);

		if (!voucher) {
			return;
		}

		setIsPending(true);

		apiCallWithAuthRetry({
			apiCall: (onError, onSuccess, refreshedHeaders) => {
				deleteCustomerAccountData(
					null,
					onError,
					onSuccess,
					refreshedHeaders || getAuthHeaders(),
					`/${accountId}/vouchers/${voucher.id}`,
				);
			},
			onError: (err) => {
				const errorMessage = err.errorResponse?.message || err.message;
				setError(errorMessage || 'Failed to delete eVoucher');
				setIsPending(false);
			},
			onSuccess: () => {
				setIsPending(false);
				setError('');
				fetchEvouchers();
			},
		});
	};

	useEffect(() => {
		setIsPending(true);
		// Get EVouchers data
		if (accountId) {
			fetchEvouchers();
		}
	}, [accountId]);

	return (
		<>
			<GlobalEVoucherStyle />
			<div className="container">
				<div className="row">
					<div className="col-12">
						<EVoucher
							isAgent
							eVouchersData={evoucherData}
							isEVoucherFormOpen={showVoucherForm}
							noItemsViewVariant="warning"
							onEVoucherFormCancel={onCancel}
							onEVoucherFormSubmit={onSubmit}
							onDelete={onDeleteEVoucher}
							onAction={() => {
								fetchCampaigns();
								setShowVoucherForm(true);
							}}
							eVoucherOptions={eVoucherOptions}
							title={intl.formatMessage(messages.lblEVouchersTitle)}
						/>
					</div>
				</div>
			</div>
			<ExtendedSnackbar
				id="srtEvoucherSnackBar"
				open={error !== ''}
				message={error}
				onClose={() => setError('')}
			/>
			{isPending && <Spinner />}
		</>
	);
};

export default injectIntl(EVouchers);
